<template>
  <div class="details-page">
    <b-modal
      v-model="showWarning"
      id="terminate-job-modal"
      title="Warning"
      hide-header-close
    >
      <template #modal-header>
        <h5 class="modal-title">Warning</h5>
        <b-button class="close" @click="showWarning = false" aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </b-button>
      </template>
      <p class="my-4">
        Are you sure you want to terminate this job? This action cannot be
        undone.
      </p>
      <template #modal-footer>
        <b-button variant="secondary" @click="showWarning = false"
          >Cancel</b-button
        >
        <b-button variant="danger" @click="handleTerminateJob"
          >Terminate</b-button
        >
      </template>
    </b-modal>

    <!-- Error Modal -->
    <b-modal
      v-model="showErrorModal"
      id="post-error-modal"
      title="Post Error Details"
      hide-header-close
      size="lg"
    >
      <template #modal-header>
        <h5 class="modal-title">Post Error Details</h5>
        <b-button
          class="close"
          @click="showErrorModal = false"
          aria-label="Close"
        >
          <span aria-hidden="true">&times;</span>
        </b-button>
      </template>
      <div v-if="selectedErrorPost" class="error-details">
        <div class="detail-row">
          <span class="detail-label">File Path:</span>
          <span class="detail-value">{{ selectedErrorPost.filePath }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Post Status:</span>
          <span class="detail-value">
            <b-badge variant="danger">{{ selectedErrorPost.status }}</b-badge>
          </span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Error Message:</span>
          <span class="detail-value error-message">{{
            selectedErrorPost.error
          }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Posted At:</span>
          <span class="detail-value">{{
            formatDate(selectedErrorPost.timestamp)
          }}</span>
        </div>
        <div v-if="selectedErrorPost.postTime" class="detail-row">
          <span class="detail-label">Scheduled Post Time:</span>
          <span class="detail-value">{{
            formatDate(selectedErrorPost.postTime)
          }}</span>
        </div>
      </div>
      <template #modal-footer>
        <b-button variant="primary" @click="showErrorModal = false"
          >Close</b-button
        >
      </template>
    </b-modal>

    <!-- Loading Spinner -->
    <template v-if="loading">
      <div class="text-center">
        <b-spinner style="width: 5rem; height: 5rem" type="grow"></b-spinner>
      </div>
    </template>

    <!-- Main Content -->
    <template v-else>
      <h5></h5>
      <!-- Navigation Buttons -->
      <div class="navigation-div">
        <b-btn
          variant="primary"
          size="sm"
          @click="navigateAutomation('previous')"
          :disabled="navigating"
        >
          <template v-if="navigating">
            <b-spinner small></b-spinner>
          </template>
          <template v-else>
            <i class="fas fa-chevron-left"></i> Previous
          </template>
        </b-btn>
        <b-btn
          variant="primary"
          size="sm"
          @click="navigateAutomation('next')"
          :disabled="navigating"
        >
          <template v-if="navigating">
            <b-spinner small></b-spinner>
          </template>
          <template v-else>
            Next <i class="fas fa-chevron-right"></i>
          </template>
        </b-btn>
      </div>

      <b-card class="shadow-sm rounded-lg border-0 mb-4 p-3">
        <div class="d-flex flex-column">
          <div class="d-flex justify-content-between">
            <h5 class="font-weight-bold mb-1">Job Details</h5>
            <div class="d-flex justify-content-between gap-3">
              <b-btn
                :variant="automation?.status === 'Running' ? 'danger' : 'dark'"
                class="text-white px-4"
                v-if="
                  automation?.status !== 'Completed' &&
                  automation?.status !== 'Terminated'
                "
                @click="toggleStatus"
                :disabled="loadingStausButton"
              >
                <template v-if="loadingStausButton">
                  <b-spinner></b-spinner>
                </template>
                <template v-else>
                  {{ automation?.status === "Running" ? "Pause" : "Resume" }}
                </template>
              </b-btn>

              <b-btn
                variant="danger"
                class="text-white px-4"
                @click="showTermitateModal"
                :disabled="
                  loadingStausButton ||
                  automation?.status === 'Terminated' ||
                  automation?.status === 'Completed'
                "
              >
                <template v-if="loadingStausButton">
                  <b-spinner></b-spinner>
                </template>
                {{
                  automation?.status === "Terminated"
                    ? "Terminated"
                    : "Termnite"
                }}
              </b-btn>
            </div>
          </div>
          <p class="text-muted small mb-3">
            View and manage the details of your scheduled job.
          </p>

          <!-- Details List -->
          <div class="d-flex justify-content-between py-1">
            <span>Job ID:</span>
            <strong>{{ automation.jobId }}</strong>
          </div>
          <div class="d-flex justify-content-between py-1">
            <span>Page ID:</span>
            <strong>{{ automation.pageId }}</strong>
          </div>
          <div
            v-if="automation.pageName"
            class="d-flex justify-content-between py-1"
          >
            <span>Page Name:</span>
            <strong>{{ automation.pageName }}</strong>
          </div>
          <div class="d-flex justify-content-between py-1">
            <span>Interval Min:</span>
            <strong>{{ automation.intervalMin }} minute(s)</strong>
          </div>
          <div class="d-flex justify-content-between py-1">
            <span>Interval Max:</span>
            <strong>{{ automation.intervalMax }} minute(s)</strong>
          </div>
          <div class="d-flex justify-content-between py-1">
            <span>Status:</span>
            <strong>{{ automation.status }}</strong>
          </div>
          <div
            v-if="automation.postType"
            class="d-flex justify-content-between py-1"
          >
            <span>Post Type:</span>
            <strong>{{ automation.postType }}</strong>
          </div>
          <div class="d-flex justify-content-between py-1">
            <span>Filename as Description:</span>
            <strong>{{
              automation.fileNameAsDescription ? "Yes" : "No"
            }}</strong>
          </div>
          <div class="d-flex justify-content-between py-1">
            <span>Created At:</span>
            <strong>{{
              formatDate(automation.createdAt) + " ( UK Time )"
            }}</strong>
          </div>

          <hr />

          <div class="d-flex justify-content-between">
            <span class="text-muted">Go To Page</span>
            <strong class="text-primary">
              <a
                class="page-link"
                :href="'https://facebook.com/' + automation.pageId"
                target="_blank"
                >{{ automation.pageName }}</a
              ></strong
            >
          </div>
        </div>
      </b-card>

      <!-- Posts Card -->
      <b-card class="shadow-sm rounded-lg border-0 p-4">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 class="font-weight-bold mb-1">Associated Posts</h5>
            <p class="text-muted small mb-0">Refresh the page for updates.</p>
          </div>
          <div class="d-flex align-items-center sort-controls">
            <b-form-select
              v-model="sortBy"
              :options="sortOptions"
              size="sm"
              class="modern-select mr-2"
            />
            <b-form-select
              v-model="sortOrder"
              :options="orderOptions"
              size="sm"
              class="modern-select"
            />
          </div>
        </div>

        <!-- Status Summary Row -->
        <div
          class="status-summary-row mb-3 p-3 rounded"
          style="background: #f8f9fa; border-left: 4px solid #0077ff"
        >
          <div class="d-flex flex-wrap gap-3">
            <b-badge
              v-for="(count, status) in statusCounts"
              :key="status"
              :variant="getBadgeVariant(status)"
              class="status-badge"
            >
              {{ status }}: {{ count }}
            </b-badge>
          </div>
        </div>

        <b-table
          hover
          small
          :items="posts"
          :fields="postFields"
          :busy="loading"
          class="modern-table"
          responsive
        >
          <template v-slot:cell(postTime)="data">
            {{ formatDate(data.item?.postTime) }}
          </template>
          <template v-slot:cell(timestamp)="data">
            {{ formatDate(data.item.timestamp) }}
          </template>
          <template v-slot:cell(collab)="data">
            <template
              v-if="['Invited', 'Clicked'].includes(data.item.collabStatus)"
            >
              <a
                @click="handleUpdateCollabStatus(data.item._id)"
                target="_blank"
                :href="'https://' + data.item.collabLink"
                >Link</a
              >
            </template>
            <div v-else>-</div>
          </template>
          <template v-slot:cell(error)="data">
            <template v-if="data.item.status === 'Failed' && data.item.error">
              <b-button
                variant="danger"
                size="sm"
                @click="showErrorDetails(data.item)"
                class="error-button"
              >
                <i class="fas fa-exclamation-circle"></i> View Error
              </b-button>
            </template>
            <div v-else>-</div>
          </template>
        </b-table>

        <div class="pagination-wrapper">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalPosts"
            :per-page="pageSize"
            align="center"
            @input="fetchPosts"
            first-text="«"
            prev-text="‹"
            next-text="›"
            last-text="»"
            size="md"
            class="custom-pagination"
          />
        </div>
      </b-card>
    </template>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";

export default {
  data() {
    return {
      id: null,
      loading: true,
      automation: {},
      loadingStausButton: false,
      navigating: false,
      showWarning: false,
      postFields: [
        { key: "filePath", label: "File Name" },
        { key: "status", label: "Status" },
        { key: "postTime", label: "Approx. Post Time" },
        { key: "error", label: "Error" },
      ],
      currentPage: 1,
      pageSize: 10,
      totalPosts: 0,
      sortBy: "timestamp",
      sortOrder: "desc",
      loading: false,
      showErrorModal: false,
      selectedErrorPost: null,
      sortOptions: [
        { value: "timestamp", text: "Timestamp" },
        { value: "status", text: "Status" },
      ],
      orderOptions: [
        { value: "asc", text: "Asc" },
        { value: "desc", text: "Desc" },
      ],
      statusCounts: {},
    };
  },
  watch: {
    sortBy() {
      this.fetchPosts();
    },
    sortOrder() {
      this.fetchPosts();
    },
  },
  mounted() {
    this.id = this.$route.params.id;
    this.getData();
  },

  methods: {
    async handleUpdateCollabStatus(postId) {
      try {
        this.loading = true;
        const response = await axios.post("/api/post/collab-clicked", {
          postId,
        });

        if (response.data.success) {
          this.$toast.success("Collab status updated successfully!");
          this.posts = this.posts.map((p) => {
            if (p._id === postId) p.collabStatus = "Clicked";
            return p;
          });
        }
      } catch (e) {
        console.log("Error occured while updating collab status");
        console.error(e);
        $toast.error("Error occured while updating collab status");
      } finally {
        this.loading = false;
      }
    },

    async getData() {
      this.loading = true;
      const response = await axios.post("/api/automations/detail", {
        _id: this.id,
      });
      this.automation = response.data.data;
      this.id = response.data.currentJobId;
      this.loading = false;

      if (this.automation.postType === "Reel") {
        this.postFields = [
          { key: "contentId", label: "Content ID" },
          ,
          ...this.postFields,
        ];
      }

      if (this.automation.collabId && this.automation.collabId !== "null") {
        this.postFields.push({ key: "collab", label: "Collab Link" });
        this.postFields.push("collabStatus");
      }

      this.fetchPosts(); // fetch post list
    },

    async fetchPosts() {
      this.loading = true;
      try {
        const res = await axios.post(
          `/api/job/posts?page=${this.currentPage}&pageSize=${this.pageSize}&sortBy=${this.sortBy}&order=${this.sortOrder}`,
          {
            jobId: this.automation.jobId,
          }
        );

        this.posts = res.data.data;
        this.totalPosts = res.data.totalPosts;
        this.statusCounts = res.data.statusCounts || {};
      } catch (err) {
        console.error("Failed to fetch posts", err);
      } finally {
        this.loading = false;
      }
    },
    formatDate(date) {
      try {
        return moment(date).format("DD-MM-YYYY HH:mm:ss");

        const _date = new Date(date).toLocaleString();

        if (_date === "Invalid Date") {
          return "Calculating";
        } else {
          return _date;
        }
      } catch (e) {
        return "-";
      }
    },
    isImage(filePath) {
      return filePath && filePath.match(/\.(jpeg|jpg|gif|png|webp)$/i);
    },

    showTermitateModal() {
      this.showWarning = true;
    },

    cancelTerminate() {
      this.showWarning = false;
    },

    handleTerminateJob() {
      this.loadingStausButton = true;

      axios
        .post(`/terminate-job/${this.automation.jobId}`)
        .then((res) => {
          if (res.data.success) {
            this.$toast.success("Job Terminated!");
            this.automation.status = "Terminated";
          }
        })
        .catch((e) => {
          this.$toast.error("Failed to Terminate Job!");
        })
        .finally((_) => {
          this.loadingStausButton = false;
          this.getData();
          this.showWarning = false;
        });
    },

    toggleStatus() {
      this.loadingStausButton = true;

      let url;
      const { status, jobId } = this.automation;

      if (status === "Running") {
        url = `/pause-job/${jobId}`;
      } else {
        url = `/resume-job/${jobId}`;
      }

      axios
        .post(url)
        .then((res) => {
          if (res.data.success) {
            this.$toast.success(`Job ${res.data.status}!`);
            this.automation.status = res.data.status;
          }
        })
        .catch((e) => {
          this.$toast.error("Failed to change Job Status!");
        })
        .finally((_) => {
          this.loadingStausButton = false;
          this.getData();
        });
    },

    async navigateAutomation(direction) {
      try {
        this.navigating = true;
        const response = await axios.post("/api/automations/navigate", {
          direction,
          currentJobId: this.id,
        });

        if (response.data.success) {
          // Update the route to the new job
          this.id = response.data.data._id;
          this.automation = response.data.data;
          this.$router.push({
            name: "AutomationDetail",
            params: { id: response.data.data._id },
          });
          this.$toast.success(`Navigated to ${direction} automation!`);
          // Reload posts for the new job
          this.currentPage = 1;
          this.fetchPosts();
        }
      } catch (error) {
        console.error("Error navigating automations:", error);
        if (error.response?.data?.message) {
          this.$toast.error(error.response.data.message);
        } else {
          this.$toast.error("Failed to navigate automations!");
        }
      } finally {
        this.navigating = false;
      }
    },

    showErrorDetails(post) {
      this.selectedErrorPost = post;
      this.showErrorModal = true;
    },

    getBadgeVariant(status) {
      const variants = {
        Published: "success",
        Failed: "danger",
        Pending: "warning",
        Clicked: "info",
        Invited: "info",
      };
      return variants[status] || "secondary";
    },
  },
};
</script>

<style scoped>
.navigation-div {
  width: 100%;
  display: flex;
  background: #fff;
  padding: 10px;
  justify-content: space-between;
  margin-bottom: 10px;
  border-radius: 10px;
}
.modern-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  margin: 1rem 0;
}

.modern-table >>> thead th {
  background: #0077ff !important;
  color: #fff !important;
  font-weight: 600;
  padding: 1rem 1.5rem;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #e2e8f0;
}

.modern-table >>> tbody td {
  padding: 1rem 1.5rem;
  color: #1e293b;
  font-size: 0.95rem;
  border-bottom: 1px solid #f1f5f9;
  background: white;
}

.modern-table >>> tbody tr:hover {
  background-color: #f8fafc;
}

.modern-table >>> tbody tr:last-child td {
  border-bottom: none;
}

/* Pagination Styling */
.pagination-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 2rem;
  padding: 0;
}

.custom-pagination {
  display: flex;
  gap: 0.25rem;
  background: #f8f8f8 !important;
  padding: 10px;
}

.custom-pagination >>> .page-item .page-link {
  border: none;
  padding: 0.5rem 0.75rem;
  color: #64748b;
  font-weight: 500;
  background: transparent;
  border-radius: 6px;
  transition: all 0.2s ease;
  margin: 5px;
}

.custom-pagination >>> .page-item:not(.disabled):hover .page-link {
  background: #f1f5f9;
  color: #0077ff;
}

.custom-pagination >>> .page-item.active .page-link {
  background: #0077ff;
  color: white;
}

.custom-pagination >>> .page-item.disabled .page-link {
  color: #cbd5e1;
  background: transparent;
}

/* Remove existing pagination styles */
.my-pagination-wrapper {
  background: none;
  padding: 0;
}

.pagination {
  background: none;
  padding: 0;
}

.sort-controls {
  gap: 0.75rem;
  padding: 0.5rem;
  border-radius: 8px;
}

.modern-select {
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.5rem 2rem 0.5rem 1rem;
  font-size: 0.9rem;
  color: #1e293b;
  background-color: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 0.75rem;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

.modern-select:hover {
  border-color: #0077ff;
}

.modern-select:focus {
  outline: none;
  border-color: #0077ff;
  box-shadow: 0 0 0 3px rgba(0, 119, 255, 0.1);
}

/* Error Modal Styles */
.error-details {
  padding: 1.5rem;
  background: #f8fafc;
  border-radius: 8px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1rem 0;
  border-bottom: 1px solid #e2e8f0;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  font-weight: 600;
  color: #1e293b;
  min-width: 150px;
}

.detail-value {
  color: #475569;
  word-break: break-word;
  flex: 1;
  text-align: right;
}

.error-message {
  color: #dc2626;
  font-weight: 500;
  background: #fee2e2;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
}

.error-button {
  white-space: nowrap;
}

.error-button i {
  margin-right: 0.25rem;
}

.status-summary-row {
  border-radius: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.status-badge {
  padding: 0.6rem 1rem !important;
  font-size: 0.9rem !important;
  font-weight: 600 !important;
  text-transform: capitalize;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: inline-block !important;
  color: white !important;
}

.status-summary-row >>> .badge-success {
  background-color: #28a745 !important;
}

.status-summary-row >>> .badge-danger {
  background-color: #dc3545 !important;
}

.status-summary-row >>> .badge-warning {
  background-color: #ffc107 !important;
  color: #333 !important;
}

.status-summary-row >>> .badge-info {
  background-color: #17a2b8 !important;
}

.status-summary-row >>> .badge-secondary {
  background-color: #6c757d !important;
}
</style>

<style>
.pagination {
  background: #fff;
  /* width: 100% !important; */
  padding: 5px 10px;
  border-radius: 5px;
}
</style>
